import * as React from "react";
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
    WithStyles
} from "@material-ui/core";
import { withStyles } from "@material-ui/styles";
import { styles } from "../../OrderDetailsView.styles";
import { useTranslation } from "react-i18next";
import { BlueButton } from "../../../components/Buttons/BlueButton";
import UniversalTable from "../../../components/UniversalTable/UniversalTable";
import { IUniversalTableColumn, IUniversalTableData } from "../../../components/UniversalTable/interfaces";
import InfoIcon from "@material-ui/icons/Info";
import CloseIcon from "@material-ui/icons/Close";
import { getIntervalText } from "../../../../utilities/modulePricingUtils";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as ModulePricingLevelsAction from "../../../../stores/products/ProductsAction";
import { selectRequesting } from "../../../../selectors/requesting/RequestingSelector";
import ModulePricingLevelModel from "../../../../models/modulePricing/ModulePricingLevelModel";

export interface IModulePricingInfoDialogProps extends WithStyles<typeof styles> {
    product: string;
}

const ModulePricingInfoDialogPure: React.FunctionComponent<IModulePricingInfoDialogProps> = ({
    classes,
    product
}: IModulePricingInfoDialogProps) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const [openDetails, setOpenDetails] = React.useState(false);

    const modulePricingLevelsFromState = useSelector(state => state.products?.modulePricingLevels);

    const isRequesting = useSelector(state =>
        selectRequesting(state, [ModulePricingLevelsAction.REQUEST_PRODUCT_MODULE_PRICING_LEVELS])
    );
    let modulePricingLevels: IUniversalTableData;

    if (modulePricingLevelsFromState) {
        modulePricingLevels = Object.values(modulePricingLevelsFromState).map((level: ModulePricingLevelModel) => ({
            lowLimit: level.lowLimit,
            highLimit: level.highLimit,
            modulePrice: level.modulePrice
        }));
    }

    useEffect(() => {
        dispatch(ModulePricingLevelsAction.requestProductModulePricingLevels(product));
        // eslint-disable-next-line
    }, []);

    const columns: IUniversalTableColumn[] = [
        {
            type: "component",
            title: "Number of modules",
            component: rowData => <>{getIntervalText(rowData?.lowLimit, rowData?.highLimit)}</>
        },
        {
            type: "price",
            title: "Licence fee per module",
            field: "modulePrice"
        }
    ];

    return (
        <>
            <Tooltip title="Price details">
                <span>
                    <IconButton onClick={() => setOpenDetails(true)} style={{ display: "inline" }}>
                        <InfoIcon />
                    </IconButton>
                </span>
            </Tooltip>
            <Dialog
                open={openDetails}
                onClose={() => setOpenDetails(false)}
                scroll="paper"
                fullWidth
                maxWidth={"sm"}
                aria-labelledby="form-dialog-title"
            >
                <DialogTitle id="form-dialog-title">
                    Pricing info
                    <IconButton
                        aria-label="close"
                        style={{ position: "absolute", right: "1px", top: "1px" }}
                        onClick={() => setOpenDetails(false)}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {modulePricingLevels && (
                            <UniversalTable
                                columns={columns}
                                data={modulePricingLevels}
                                dense
                                pagingDisabled
                                isLoading={isRequesting}
                            />
                        )}
                        <Typography variant="caption">
                            <p>One module consists of 31 business days or a part thereof per product ordered.</p>
                            <p>
                                The licence fee per module is determined according to the 360T fee matrix by the
                                consolidated number of modules over all products ordered.
                            </p>
                            <p>The consolidation encompasses all 360T products.</p>
                        </Typography>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <BlueButton onClick={() => setOpenDetails(false)}>{t("general.close")}</BlueButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

export const ModulePricingInfoDialog = withStyles(styles)(ModulePricingInfoDialogPure);
